import React, { useState, useEffect } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement,
  ArcElement, Title, Tooltip, Legend,
} from 'chart.js';
import apiClient from '../services/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

/**
 * KpiAnalytics.jsx — KPI Analytics page
 * ສະແດງ KPI ລວມທຸກໂມດູນ, ປຽບທຽບ, ອັດຕາເຕີບໂຕ
 */
const KpiAnalytics = ({ lang = 'lo', theme = 'dark' }) => {
  const [kpiData, setKpiData]   = useState(null);
  const [modules, setModules]   = useState([]);
  const [days, setDays]         = useState(30);
  const [loading, setLoading]   = useState(true);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');

  const i18n = {
    lo: {
      title: 'ວິເຄາະ KPI',
      subtitle: 'ຕົວຊີ້ວັດຫຼັກທຸກໂມດູນ',
      totalRevenue: 'ລາຍຮັບລວມ',
      totalTxn: 'ທຸລະກຳທັງໝົດ',
      avgPerTxn: 'ສະເລ່ຍ/ທຸລະກຳ',
      topModule: 'ໂມດູນສູງສຸດ',
      moduleBreakdown: 'ລາຍຮັບຕາມໂມດູນ',
      comparison: 'ປຽບທຽບໂມດູນ',
      share: 'ສ່ວນແບ່ງ',
      revenue: 'ລາຍຮັບ',
      transactions: 'ທຸລະກຳ',
      today: 'ວັນນີ້', day7: '7 ວັນ', day30: '30 ວັນ', day90: '90 ວັນ',
      toll: 'ຄ່າທາງດ່ວນ', bridge: 'ຄ່າຂ້າມສະພານ', wim: 'ຄ່າປັບນ້ຳໜັກ', auction: 'ປະມູນປ້າຍ', topup: 'OBU ອັດຕາເຕີມ',
      customRange: 'ກຳນົດເອງ',
      fromDate: 'ຈາກວັນທີ',
      toDate: 'ຫາວັນທີ',
      apply: 'ນຳໃຊ້',
    },
    th: {
      title: 'วิเคราะห์ KPI',
      subtitle: 'ตัวชี้วัดหลักทุกโมดูล',
      totalRevenue: 'รายได้รวม',
      totalTxn: 'ธุรกรรมทั้งหมด',
      avgPerTxn: 'เฉลี่ย/ธุรกรรม',
      topModule: 'โมดูลสูงสุด',
      moduleBreakdown: 'รายได้ตามโมดูล',
      comparison: 'เปรียบเทียบโมดูล',
      share: 'สัดส่วน',
      revenue: 'รายได้',
      transactions: 'ธุรกรรม',
      today: 'วันนี้', day7: '7 วัน', day30: '30 วัน', day90: '90 วัน',
      toll: 'ค่าทางด่วน', bridge: 'ค่าข้ามสะพาน', wim: 'ค่าปรับน้ำหนัก', auction: 'ประมูลป้าย', topup: 'อัดเติม OBU',
      customRange: 'กำหนดเอง',
      fromDate: 'จากวันที่',
      toDate: 'ถึงวันที่',
      apply: 'นำไปใช้',
    },
    en: {
      title: 'KPI Analytics',
      subtitle: 'Key performance indicators across all modules',
      totalRevenue: 'Total Revenue',
      totalTxn: 'Total Transactions',
      avgPerTxn: 'Avg / Transaction',
      topModule: 'Top Module',
      moduleBreakdown: 'Revenue by Module',
      comparison: 'Module Comparison',
      share: 'Share',
      revenue: 'Revenue',
      transactions: 'Transactions',
      today: 'Today', day7: '7 Days', day30: '30 Days', day90: '90 Days',
      toll: 'Toll Fee', bridge: 'Bridge Fee', wim: 'WIM Fine', auction: 'Plate Auction', topup: 'OBU TopUp',
      customRange: 'Custom Range',
      fromDate: 'From Date',
      toDate: 'To Date',
      apply: 'Apply',
    },
  };
  const t = (k) => i18n[lang]?.[k] || i18n.en[k] || k;

  const MODULE_META = {
    toll:    { color: '#fa8c16', label: { lo: 'ຄ່າທາງດ່ວນ',    th: 'ค่าทางด่วน',    en: 'Toll Fee' } },
    bridge:  { color: '#1890ff', label: { lo: 'ຄ່າຂ້າມສະພານ', th: 'ค่าข้ามสะพาน', en: 'Bridge Fee' } },
    wim:     { color: '#f5222d', label: { lo: 'ຄ່າປັບນ້ຳໜັກ', th: 'ค่าปรับน้ำหนัก', en: 'WIM Fine' } },
    auction: { color: '#52c41a', label: { lo: 'ປະມູນປ້າຍ',    th: 'ประมูลป้าย',    en: 'Plate Auction' } },
    topup:   { color: '#9254de', label: { lo: 'OBU ອັດຕາເຕີມ', th: 'อัดเติม OBU',   en: 'OBU TopUp' } },
  };

  const fmt  = (n) => Number(n || 0).toLocaleString();
  const fmtB = (v) => {
    if (v === undefined || v === null) return '₭0';
    const num = Number(v);
    if (isNaN(num)) return '₭0';
    if (num >= 1e9) {
      return `₭${parseFloat((num / 1e9).toFixed(2))}B`;
    } else if (num >= 1e6) {
      return `₭${parseFloat((num / 1e6).toFixed(2))}M`;
    } else {
      return `₭${num.toLocaleString()}`;
    }
  };
  const fmtK = (n) => `₭ ${(Number(n || 0) / 1e3).toFixed(0)}K`;

  const loadKpiData = async (params = {}) => {
    setLoading(true);
    try {
      const [kpiRes, modRes] = await Promise.all([
        apiClient.dashboardGetKpi(params),
        apiClient.dashboardGetModules(params),
      ]);
      if (kpiRes?.success)  setKpiData(kpiRes.data);
      if (modRes?.success && Array.isArray(modRes.data)) setModules(modRes.data);
    } catch (e) {
      console.warn('KPI load error:', e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (days === 'customRange') return;
    const params = days !== 'all' ? { days } : {};
    loadKpiData(params);
  }, [days]);

  const handleCustomRangeApply = () => {
    if (!customStart || !customEnd) return;
    setDays('customRange');
    loadKpiData({ startDate: customStart, endDate: customEnd });
  };

  const gridColor = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const tickColor = theme === 'dark' ? '#8892a4' : '#546e7a';
  const tooltip   = { backgroundColor: 'rgba(255,255,255,0.96)', titleColor: '#2c3e50', bodyColor: '#546e7a', borderColor: '#e1e4e8', borderWidth: 1, padding: 12 };

  const kpiEntries = kpiData ? Object.entries(kpiData) : [];
  const totalRevenue = kpiEntries.reduce((s, [, v]) => s + (v?.total || 0), 0);
  const totalTxn     = kpiEntries.reduce((s, [, v]) => s + (v?.transactions || 0), 0);
  const avgPerTxn    = totalTxn > 0 ? totalRevenue / totalTxn : 0;
  const topEntry     = kpiEntries.sort((a, b) => (b[1]?.total || 0) - (a[1]?.total || 0))[0];
  const topModKey    = topEntry?.[0];
  const topModName   = topModKey ? (MODULE_META[topModKey]?.label[lang] || topModKey) : '-';

  const moduleRows = kpiEntries.map(([key, v]) => ({
    key,
    color: MODULE_META[key]?.color || '#888',
    label: MODULE_META[key]?.label[lang] || key,
    revenue: v?.total || 0,
    txn: v?.transactions || 0,
    share: totalRevenue > 0 ? ((v?.total || 0) / totalRevenue * 100).toFixed(1) : '0',
    avg: (v?.transactions || 0) > 0 ? Math.round((v?.total || 0) / (v?.transactions || 0)) : 0,
  })).sort((a, b) => b.revenue - a.revenue);

  const barData = {
    labels: moduleRows.map(m => m.label),
    datasets: [
      {
        label: t('revenue'),
        data: moduleRows.map(m => m.revenue / 1e9),
        backgroundColor: moduleRows.map(m => m.color + 'cc'),
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };
  const barOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { ...tooltip, callbacks: { label: v => `₭ ${v.raw.toFixed(2)}B` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: tickColor, font: { size: 11 } } },
      y: { grid: { color: gridColor }, ticks: { color: tickColor, callback: v => `₭${v}B` } },
    },
  };

  const doughnutData = {
    labels: moduleRows.map(m => m.label),
    datasets: [{
      data: moduleRows.map(m => parseFloat(m.share)),
      backgroundColor: moduleRows.map(m => m.color + 'cc'),
      borderWidth: 0,
      hoverOffset: 6,
    }],
  };
  const doughnutOptions = {
    responsive: true, maintainAspectRatio: false, cutout: '68%',
    plugins: {
      legend: { display: false },
      tooltip: { ...tooltip, callbacks: { label: v => `${v.label}: ${v.raw}%` } },
    },
  };

  const summaryKpis = [
    { label: t('totalRevenue'), val: fmtB(totalRevenue), sub: days === 'customRange' ? `${customStart} ~ ${customEnd}` : `${days} days`, color: '#1890ff' },
    { label: t('totalTxn'),     val: fmt(totalTxn),       sub: 'transactions', color: '#52c41a' },
    { label: t('avgPerTxn'),    val: fmtK(avgPerTxn),     sub: 'per txn',      color: '#9254de' },
    { label: t('topModule'),    val: topModName,           sub: topEntry ? `${((topEntry[1]?.total || 0) / totalRevenue * 100).toFixed(1)}% share` : '-', color: topModKey ? MODULE_META[topModKey]?.color : '#fa8c16' },
  ];

  return (
    <div className="main">
      <div className="section-header" style={{ flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div className="section-title">{t('title')}</div>
          <div className="section-sub">{t('subtitle')}</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {[{v:1,l:t('today')},{v:7,l:t('day7')},{v:30,l:t('day30')},{v:90,l:t('day90')}].map(d => (
            <div key={d.v} className={`filter-chip ${days === d.v ? 'active' : ''}`}
              onClick={() => setDays(d.v)} style={{ cursor: 'pointer' }}>
              {d.l}
            </div>
          ))}
          <div style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 8px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{t('fromDate')}:</span>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                style={{
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: 'var(--text)',
                  fontFamily: 'var(--font)',
                  outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{t('toDate')}:</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                style={{
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: 'var(--text)',
                  fontFamily: 'var(--font)',
                  outline: 'none',
                }}
              />
            </div>
            <button
              onClick={handleCustomRangeApply}
              disabled={!customStart || !customEnd}
              style={{
                background: 'var(--blue)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                padding: '5px 12px',
                fontSize: '12px',
                cursor: (!customStart || !customEnd) ? 'not-allowed' : 'pointer',
                opacity: (!customStart || !customEnd) ? 0.5 : 1,
                fontFamily: 'var(--font)',
                fontWeight: '600',
                transition: 'all 0.2s ease',
              }}
            >
              {t('apply')}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)' }}>Loading...</div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="kpi-grid" style={{ marginBottom: '16px' }}>
            {summaryKpis.map(k => (
              <div key={k.label} className="kpi">
                <div className="kpi-accent" style={{ background: k.color }} />
                <div className="kpi-label">{k.label}</div>
                <div className="kpi-value" style={{ color: k.color }}>{k.val}</div>
                <div className="kpi-sub kpi-up">{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Charts row */}
          <div className="chart-row" style={{ marginBottom: '16px' }}>
            <div className="card">
              <div className="card-title">{t('moduleBreakdown')}</div>
              <div className="card-sub">₭ Billion</div>
              <div style={{ height: '220px', marginTop: '12px' }}>
                <Bar data={barData} options={barOptions} />
              </div>
            </div>
            <div className="card">
              <div className="card-title">{t('share')}</div>
              <div className="card-sub">% {lang === 'lo' ? 'ສ່ວນແບ່ງ' : lang === 'th' ? 'สัดส่วน' : 'of total'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'center', marginTop: '12px' }}>
                <div style={{ height: '180px' }}>
                  <Doughnut data={doughnutData} options={doughnutOptions} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {moduleRows.map(m => (
                    <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text)' }}>{m.label}</div>
                        <div style={{ fontSize: '10px', color: 'var(--text3)' }}>{fmtB(m.revenue)}</div>
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: m.color }}>{m.share}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Module detail table */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)' }}>
              <div className="card-title">{t('comparison')}</div>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>{lang === 'lo' ? 'ໂມດູນ' : lang === 'th' ? 'โมดูล' : 'Module'}</th>
                  <th style={{ textAlign: 'right' }}>{t('revenue')}</th>
                  <th style={{ textAlign: 'right' }}>{t('transactions')}</th>
                  <th style={{ textAlign: 'right' }}>{t('avgPerTxn')}</th>
                  <th style={{ textAlign: 'right' }}>{t('share')}</th>
                  <th style={{ width: '140px' }}></th>
                </tr>
              </thead>
              <tbody>
                {moduleRows.map(m => (
                  <tr key={m.key}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: m.color }} />
                        <span style={{ fontWeight: '500' }}>{m.label}</span>
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: '600', color: m.color }}>{fmtB(m.revenue)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>{fmt(m.txn)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: '12px', color: 'var(--text3)' }}>₭ {fmt(m.avg)}</td>
                    <td style={{ textAlign: 'right', fontWeight: '600', color: m.color }}>{m.share}%</td>
                    <td>
                      <div style={{ height: '6px', borderRadius: '3px', background: 'var(--bg4)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: '3px', background: m.color, width: `${m.share}%`, transition: 'width 0.6s ease' }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default KpiAnalytics;
